import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import { Switch } from "@/components/ui/switch";
import type {
  AccessGroupListItem,
  AccessGroupScope,
} from "@/types/access-group";

const renderMutedText = (value: React.ReactNode) => (
  <span className="font-sans text-[10px] leading-4 font-medium tracking-normal text-neutral-500">
    {value}
  </span>
);

type GroupsTableProps = {
  rows: AccessGroupListItem[];
  isLoading: boolean;
  scope: AccessGroupScope;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  sortField: string | null;
  sortDirection: "asc" | "desc" | null;
  onSort: (field: string) => void;
  canEdit: boolean;
  onEdit: (id: string) => void;
  updatingGroupId?: string | null;
  onToggleStatus?: (id: string, isActive: boolean) => Promise<void>;
  onRowClick?: (id: string) => void;
};

export const GroupsTable: FC<GroupsTableProps> = ({
  rows,
  isLoading,
  scope,
  currentPage,
  totalPages,
  onPageChange,
  sortField,
  sortDirection,
  onSort,
  canEdit,
  onEdit,
  updatingGroupId,
  onToggleStatus,
  onRowClick,
}) => {
  const { t } = useTranslation();

  const columns = useMemo<Column<AccessGroupListItem>[]>(() => {
    const statusColumns: Column<AccessGroupListItem>[] =
      scope === "tenant"
        ? [
            {
              key: "isActive",
              header: t("users.groups.table.status"),
              sortable: true,
              className: "text-center py-0",
              render: (_, row) => {
                if (!("isActive" in row)) {
                  return null;
                }

                const title = row.isActive
                  ? t("users.groups.table.deactivateGroup")
                  : t("users.groups.table.activateGroup");

                return (
                  <div
                    className="flex items-center justify-center"
                    onClick={(event) => {
                      if (canEdit && onToggleStatus) {
                        event.stopPropagation();
                      }
                    }}
                    title={title}
                  >
                    <Switch
                      checked={row.isActive}
                      disabled={
                        !canEdit ||
                        !onToggleStatus ||
                        updatingGroupId === row.id
                      }
                      className={
                        !canEdit || !onToggleStatus
                          ? "pointer-events-none"
                          : undefined
                      }
                      onCheckedChange={(isActive) => {
                        onToggleStatus?.(row.id, isActive);
                      }}
                      aria-label={title}
                    />
                  </div>
                );
              },
            },
          ]
        : [];
    const scopedColumns: Column<AccessGroupListItem>[] =
      scope === "admin"
        ? [
            {
              key: "tenantCount",
              header: t("users.groups.table.tenants", "Tenants"),
              sortable: true,
              className: "text-center py-0",
              render: (value) =>
                renderMutedText(
                  t("users.groups.table.tenantsCount", "{{count}} tenants", {
                    count: value as number,
                  }),
                ),
            },
          ]
        : [
            {
              key: "hotelCount",
              header: t("users.groups.table.hotel"),
              sortable: true,
              className: "text-center py-0",
              render: (value) => renderMutedText(value),
            },
          ];

    return [
      ...statusColumns,
      {
        key: "name",
        header: t("users.groups.table.group"),
        sortable: true,
        className: "text-start py-0",
        render: (value) => renderMutedText(value),
      },
      ...scopedColumns,
      {
        key: "memberCount",
        header: t("users.groups.table.users", "Users"),
        sortable: true,
        className: "text-center py-0",
        render: (value) =>
          renderMutedText(
            t("users.groups.table.usersCount", "{{count}} users", {
              count: value as number,
            }),
          ),
      },
      {
        key: "actions",
        header: t("users.groups.table.actions"),
        sortable: false,
        className: "text-end py-0",
        render: (_, row) => (
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(event) => {
                event.stopPropagation();
                onEdit(row.id);
              }}
              title={
                canEdit
                  ? t("users.groups.table.editGroup", "Edit group")
                  : t(
                      "common.insufficientPermissions",
                      "Insufficient permissions",
                    )
              }
              disabled={!canEdit}
            >
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ];
  }, [canEdit, onEdit, onToggleStatus, scope, t, updatingGroupId]);

  return (
    <DataTable
      data={rows}
      columns={columns}
      loading={isLoading}
      onRowClick={onRowClick ? (row) => onRowClick(row.id) : undefined}
      withSorting
      sorting={{
        field: sortField ?? "",
        direction: sortDirection,
        onSort,
      }}
      columnHeaderClassName="inline-flex h-10 items-center gap-1 px-2 font-sans text-[10px] font-medium leading-4 tracking-normal text-neutral-500"
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
      }}
      emptyMessage={t("users.groups.table.noGroupsFound")}
    />
  );
};
